import { CarGroupSection } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

import { CarListSection } from '../CarList/CarListSection';
import { CarSection } from './CarSection';
import { DualCarsSection } from './DualCarsSection';
import { MultiCarsSection } from './MultiCarsSection';

interface CarWarrantySectionProps {
  section: CarGroupSection;
  getValidation: (identifier: string) => ValidationResult;
  onValidationChange: (
    identifier: string,
    validation: ValidationResult,
  ) => void;
  selectedSectionUuid: string | null;
  onTableClick: (sectionUuid: string) => void;
  setRef: (identifier: string, el: HTMLDivElement | null) => void;
}

export const CarWarrantySection = ({
  section,
  getValidation,
  onValidationChange,
  selectedSectionUuid,
  onTableClick,
  setRef,
}: CarWarrantySectionProps) => {
  const carListIdentifier = `${section.identifier}-carlist`;
  const numCars = section.cars.length;

  return (
    <>
      {/* Car List Section - Shows all cars */}
      <CarListSection
        key={carListIdentifier}
        ref={(el) => setRef(carListIdentifier, el)}
        cars={section.cars}
        title={section.title}
        carListIdentifier={carListIdentifier}
        validation={getValidation(carListIdentifier)}
        onValidationChange={(validation) =>
          onValidationChange(carListIdentifier, validation)
        }
        isSelected={selectedSectionUuid === carListIdentifier}
        onTableClick={onTableClick}
      />

      {/* For 1 car: Show individual section */}
      {numCars === 1 &&
        section.cars.map((car, carIndex) => {
          const carIdentifier = `${section.identifier}-car-${carIndex}`;
          return (
            <CarSection
              key={carIdentifier}
              ref={(el) => setRef(carIdentifier, el)}
              car={car}
              carIdentifier={carIdentifier}
              validation={getValidation(carIdentifier)}
              onValidationChange={(validation) =>
                onValidationChange(carIdentifier, validation)
              }
              isSelected={selectedSectionUuid === carIdentifier}
              onTableClick={onTableClick}
            />
          );
        })}

      {/* For exactly 2 cars: Show side-by-side comparison */}
      {numCars === 2 && (
        <DualCarsSection
          key={`${section.identifier}-dual`}
          ref={(el) => setRef(`${section.identifier}-dual`, el)}
          car1={section.cars[0]}
          car2={section.cars[1]}
          carIdentifier={`${section.identifier}-dual`}
          validation={getValidation(`${section.identifier}-dual`)}
          onValidationChange={(validation) =>
            onValidationChange(`${section.identifier}-dual`, validation)
          }
          isSelected={selectedSectionUuid === `${section.identifier}-dual`}
          onTableClick={onTableClick}
        />
      )}

      {/* For 3+ cars: Show transposed table grouped by chapter */}
      {numCars >= 3 && (
        <MultiCarsSection
          key={`${section.identifier}-multi`}
          ref={(el) => setRef(`${section.identifier}-multi`, el)}
          cars={section.cars}
          carIdentifier={`${section.identifier}-multi`}
          validation={getValidation(`${section.identifier}-multi`)}
          onValidationChange={(validation) =>
            onValidationChange(`${section.identifier}-multi`, validation)
          }
          isSelected={selectedSectionUuid === `${section.identifier}-multi`}
          onTableClick={onTableClick}
        />
      )}
    </>
  );
};
